import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Checkbox,
  Text,
  Stack,
  List,
  ListItem,
  Input,
  FormLabel,
} from '@chakra-ui/react';
import { pluck, reject, flatten, any, all } from 'ramda';

interface DataShape {
  name: string;
  id?: string;
  children?: DataShape[];
}

interface PeerSelectorProps {
  data: DataShape[];
  defaultSelectedData?: string[];
  onChange: (selectedData: string[]) => void;
}

const PeerSelector: React.FC<PeerSelectorProps> = ({
  data,
  defaultSelectedData = [],
  onChange,
}) => {
  const [selectedData, setSelectedData] = useState<string[]>(
    defaultSelectedData,
  );

  const [search, setSearch] = useState('');

  const itemShouldBe = (
    dataItem: DataShape,
    type: 'selected' | 'indeterminate',
  ) => {
    if (!dataItem.children) {
      return false;
    }
    const result = dataItem.children.map((child) => {
      if (child.children) {
        const childIDs = pluck('id', child.children);
        const areIncluded = childIDs.map((childId) =>
          selectedData.includes(childId || ''),
        );

        return type === 'selected'
          ? all((i) => !!i, areIncluded)
          : any((i) => !!i, areIncluded);
      }
      return selectedData.includes(child.id || '');
    });

    return type === 'selected'
      ? all((i) => !!i, result)
      : any((i) => !!i, result);
  };

  const handleChange = (clickedDataShape: DataShape) => {
    if (clickedDataShape.children) {
      const childIds = pluck('id', clickedDataShape.children).filter(
        Boolean,
      ) as string[];

      /**
       * childIds will be `[]` when the clickedDataShape is a continent. In this
       * case we should go one level deeper into the data structure in order to
       * fetch these IDs.
       */
      if (childIds.length === 0) {
        const innerChildIds = pluck<string, any>(
          'id',
          flatten(clickedDataShape.children.map((child) => child.children)),
        ).filter(Boolean) as string[];

        if (!itemShouldBe(clickedDataShape, 'selected')) {
          setSelectedData([...selectedData, ...innerChildIds]);
          return;
        }

        setSelectedData([
          ...reject((id: string) => innerChildIds.includes(id), selectedData),
        ]);
        return;
      }

      if (!itemShouldBe(clickedDataShape, 'selected')) {
        setSelectedData([...selectedData, ...childIds]);
        return;
      }

      setSelectedData([
        ...reject((id: string) => childIds.includes(id), selectedData),
      ]);
      return;
    }

    if (selectedData.includes(clickedDataShape.id as string)) {
      setSelectedData(selectedData.filter((id) => id !== clickedDataShape.id));
      return;
    }

    setSelectedData([...selectedData, clickedDataShape.id as string]);
  };

  const allInstitutions = flatten(
    data.map((continent) =>
      continent.children?.map((country) =>
        country.children?.map((institution) => institution),
      ),
    ),
  );
  const selectedInstitutions = selectedData
    .map((id) =>
      allInstitutions.find(
        (institute) => institute?.id === id && institute?.name,
      ),
    )
    .filter(Boolean);

  const allIds = flatten(
    data.map((continent) =>
      continent.children?.map((country) =>
        country.children ? pluck('id', country.children) : null,
      ),
    ),
  ) as string[];

  const handleSelectAll = () => {
    if (allIds.length === selectedData.length) {
      setSelectedData([]);
      return;
    }

    setSelectedData(allIds);
  };
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    onChange(selectedData);
  }, [selectedData, onChange]);

  const filterInstitutionsBySearch = () => {
    const isMatch = (name: string) =>
      name.toLowerCase().includes(search.toLowerCase());

    return data
      .map((continent) => {
        const countriesThatMatchSearch = continent.children?.map((country) =>
          country.children?.filter(
            (institution) =>
              isMatch(institution.name) ||
              isMatch(country.name) ||
              isMatch(continent.name),
          ),
        );

        if (
          countriesThatMatchSearch &&
          flatten(countriesThatMatchSearch).length < 1
        ) {
          return null;
        }

        return {
          ...continent,
          children: continent.children
            ?.map((country) => {
              const childrenThatMatchSearch = country.children?.filter(
                (institution) =>
                  isMatch(institution.name) ||
                  isMatch(country.name) ||
                  isMatch(continent.name),
              );
              return childrenThatMatchSearch &&
                childrenThatMatchSearch.length > 0
                ? {
                    ...country,
                    children: childrenThatMatchSearch,
                  }
                : null;
            })
            .filter(Boolean),
        };
      })
      .filter(Boolean);
  };

  const dataToUse = (search
    ? filterInstitutionsBySearch()
    : data) as DataShape[];

  return (
    <Flex color="white">
      <Flex flexDirection="column" flex={1}>
        <FormLabel mb={4}>
          Search by institution name
          <br />
          <Input
            mt={1}
            size="sm"
            onChange={handleSearch}
            value={search}
            data-testid="searchBox"
            bg="gray.700"
            _hover={{
              bg: 'gray.700',
              borderBottom: '2px solid',
              borderColor: 'yellow.500',
            }}
            _focus={{
              bg: 'gray.700',
              borderBottom: '2px solid',
              borderColor: 'yellow.500',
            }}
            border="none"
            maxW={64}
          />
        </FormLabel>
        <Box
          mr={4}
          overflow="auto"
          maxH={{
            base: '250px',
            md: '450px',
          }}
        >
          <Checkbox
            value="Worldwide"
            isIndeterminate={
              selectedData.length > 0 && allIds.length !== selectedData.length
            }
            isChecked={allIds.length === selectedData.length}
            onChange={() => handleSelectAll()}
          >
            Worldwide
          </Checkbox>
          {dataToUse.map((parent) => (
            <Stack key={parent.name} pl={6} mt={1} spacing={1}>
              <Checkbox
                value={parent.name}
                isIndeterminate={
                  itemShouldBe(parent, 'indeterminate') &&
                  !itemShouldBe(parent, 'selected')
                }
                isChecked={itemShouldBe(parent, 'selected')}
                onChange={() => handleChange(parent)}
              >
                {parent.name}
              </Checkbox>
              {parent.children?.map((item) => (
                <Stack key={item.name} pl={6} mt={1} spacing={1}>
                  <Checkbox
                    isChecked={itemShouldBe(item, 'selected')}
                    isIndeterminate={
                      itemShouldBe(item, 'indeterminate') &&
                      !itemShouldBe(item, 'selected')
                    }
                    value={item.name}
                    onChange={() => handleChange(item)}
                  >
                    {item.name}
                  </Checkbox>
                  {item.children?.map((child) => {
                    const isChecked = child?.id
                      ? selectedData.includes(child.id)
                      : false;
                    return (
                      <Stack key={child.name} pl={6} mt={1} spacing={1}>
                        <Checkbox
                          isChecked={isChecked}
                          onChange={() => handleChange(child)}
                          value={child.name}
                        >
                          {child.name}
                        </Checkbox>
                      </Stack>
                    );
                  })}
                </Stack>
              ))}
            </Stack>
          ))}
        </Box>
      </Flex>
      <Box
        display={{ base: 'none', md: 'block' }}
        flex={1}
        overflow="auto"
        maxH="524"
      >
        <List spacing={1} ml={16} mt={75}>
          <Text as="h3" mb={2}>
            {`You have selected ${selectedInstitutions.length} of a possible ${allInstitutions.length}`}
          </Text>
          {selectedInstitutions.map((institution, index) => (
            <ListItem
              as={Flex}
              key={index}
              data-testid={institution?.name}
              alignItems="center"
            >
              <Checkbox
                mr={2}
                isChecked={true}
                onChange={() => handleChange(institution as DataShape)}
              />
              {institution?.name}
            </ListItem>
          ))}
        </List>
      </Box>
    </Flex>
  );
};

export default PeerSelector;
